<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import ContactAPI from 'dashboard/api/contacts';
import { isLabelInCategory } from 'dashboard/constants/labelCategories';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const emit = defineEmits(['save', 'remove']);

const { t } = useI18n();
const store = useStore();
const agents = useMapGetter('agents/getAgents');
const labels = useMapGetter('labels/getLabels');

const dialogRef = ref(null);
const editingId = ref(null);
const editingPublicId = ref(null);

// Labels assignable to reminders (global or reminder-categorised), plus any
// already selected on this reminder so they stay visible/removable.
const availableLabels = computed(() =>
  (labels.value || []).filter(
    label =>
      isLabelInCategory(label, 'reminder') || form.labels.includes(label.title)
  )
);

// Default the trigger to one hour from now, rounded to the minute.
const defaultTriggerAt = () => {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const emptyForm = () => ({
  title: '',
  description: '',
  triggerType: 'time', // only 'time' supported for now
  triggerAt: defaultTriggerAt(),
  agentIds: [],
  contacts: [], // [{ id, name }]
  labels: [], // [title]
});

const form = reactive(emptyForm());

const isEditing = computed(() => Boolean(editingId.value));
const isFormInvalid = computed(
  () => !form.title.trim() || !form.triggerAt
);

const contactSearch = ref('');
const contactResults = ref([]);
const isSearching = ref(false);

const resetForm = () => {
  Object.assign(form, emptyForm());
  editingId.value = null;
  editingPublicId.value = null;
  contactSearch.value = '';
  contactResults.value = [];
};

const toggleAgent = id => {
  form.agentIds = form.agentIds.includes(id)
    ? form.agentIds.filter(a => a !== id)
    : [...form.agentIds, id];
};

const toggleLabel = title => {
  form.labels = form.labels.includes(title)
    ? form.labels.filter(l => l !== title)
    : [...form.labels, title];
};

const labelColor = title =>
  (labels.value || []).find(l => l.title === title)?.color || '#9ca3af';

const searchContacts = async () => {
  const q = contactSearch.value.trim();
  if (!q) {
    contactResults.value = [];
    return;
  }
  isSearching.value = true;
  try {
    const { data } = await ContactAPI.search(q, 1, 'name', '');
    contactResults.value = (data.payload || []).slice(0, 8);
  } catch {
    contactResults.value = [];
  } finally {
    isSearching.value = false;
  }
};

const addContact = contact => {
  if (!form.contacts.some(c => c.id === contact.id)) {
    form.contacts = [...form.contacts, { id: contact.id, name: contact.name }];
  }
  contactSearch.value = '';
  contactResults.value = [];
};

const removeContact = id => {
  form.contacts = form.contacts.filter(c => c.id !== id);
};

const openForCreate = (prefill = {}) => {
  resetForm();
  if (prefill.contact) form.contacts = [prefill.contact];
  store.dispatch('agents/get');
  store.dispatch('labels/get');
  dialogRef.value?.open();
};

const openForEdit = reminder => {
  resetForm();
  editingId.value = reminder.id;
  editingPublicId.value = reminder.publicId || null;
  Object.assign(form, {
    title: reminder.title,
    description: reminder.description || '',
    triggerType: reminder.triggerType || 'time',
    triggerAt: reminder.triggerAt || defaultTriggerAt(),
    agentIds: [...(reminder.agentIds || [])],
    contacts: [...(reminder.contacts || [])],
    labels: [...(reminder.labels || [])],
  });
  store.dispatch('agents/get');
  store.dispatch('labels/get');
  dialogRef.value?.open();
};

const handleConfirm = () => {
  if (isFormInvalid.value) return;
  const agentList = (agents.value || []).filter(a =>
    form.agentIds.includes(a.id)
  );
  emit('save', {
    id: editingId.value,
    title: form.title.trim(),
    description: form.description.trim(),
    triggerType: 'time',
    triggerAt: form.triggerAt,
    agentIds: [...form.agentIds],
    agents: agentList.map(a => ({ id: a.id, name: a.name })),
    contacts: [...form.contacts],
    contactIds: form.contacts.map(c => c.id),
    labels: [...form.labels],
    color: form.labels.length ? labelColor(form.labels[0]) : null,
  });
};

const handleRemove = () => {
  if (editingId.value) emit('remove', editingId.value);
};

const onSuccess = () => {
  dialogRef.value?.close();
  resetForm();
};

defineExpose({ openForCreate, openForEdit, onSuccess });

const fieldClass =
  'w-full h-9 px-3 rounded-lg text-sm bg-n-alpha-black2 text-n-slate-12 border border-n-weak outline-none focus:border-n-brand';
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    @confirm="handleConfirm"
    @close="resetForm"
  >
    <div class="flex flex-col gap-5">
      <span class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
        {{
          isEditing
            ? t('REMINDERS.DIALOG.EDIT_TITLE')
            : t('REMINDERS.DIALOG.CREATE_TITLE')
        }}
        <span
          v-if="editingPublicId"
          class="text-[11px] font-mono px-1.5 py-0.5 rounded-md bg-n-alpha-2 text-n-slate-10"
        >
          {{ editingPublicId }}
        </span>
      </span>

      <label class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-11">{{
          t('REMINDERS.DIALOG.TITLE')
        }}</span>
        <Input
          v-model="form.title"
          :placeholder="t('REMINDERS.DIALOG.TITLE_PLACEHOLDER')"
          autofocus
        />
      </label>

      <!-- Trigger -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">{{
            t('REMINDERS.DIALOG.TRIGGER_TYPE')
          }}</span>
          <select v-model="form.triggerType" :class="fieldClass" disabled>
            <option value="time">
              {{ t('REMINDERS.DIALOG.TRIGGER_TYPE_TIME') }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">{{
            t('REMINDERS.DIALOG.DATETIME')
          }}</span>
          <input
            v-model="form.triggerAt"
            type="datetime-local"
            :class="fieldClass"
          />
        </label>
      </div>

      <!-- Assigned Dotchat users -->
      <div class="flex flex-col gap-2">
        <span class="text-sm text-n-slate-11">
          {{ t('REMINDERS.DIALOG.ASSIGN_USERS') }}
        </span>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="agent in agents"
            :key="agent.id"
            type="button"
            class="px-3 py-1.5 rounded-lg text-xs border transition-colors"
            :class="
              form.agentIds.includes(agent.id)
                ? 'bg-n-brand/10 border-n-brand text-n-slate-12'
                : 'bg-n-alpha-black2 border-n-weak text-n-slate-11 hover:border-n-slate-7'
            "
            @click="toggleAgent(agent.id)"
          >
            {{ agent.name }}
          </button>
          <span v-if="!agents.length" class="text-xs text-n-slate-10">
            {{ t('REMINDERS.DIALOG.NO_USERS') }}
          </span>
        </div>
      </div>

      <!-- Reference contacts -->
      <div class="flex flex-col gap-2">
        <span class="text-sm text-n-slate-11">{{
          t('REMINDERS.DIALOG.REFERENCE_CONTACTS')
        }}</span>
        <div v-if="form.contacts.length" class="flex flex-wrap gap-2">
          <span
            v-for="contact in form.contacts"
            :key="contact.id"
            class="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-n-solid-3 text-n-slate-12"
          >
            {{ contact.name }}
            <button
              type="button"
              class="i-lucide-x size-3 text-n-slate-10 hover:text-n-slate-12"
              @click="removeContact(contact.id)"
            />
          </span>
        </div>
        <div class="relative">
          <input
            v-model="contactSearch"
            type="text"
            :class="fieldClass"
            :placeholder="t('REMINDERS.DIALOG.SEARCH_CONTACT_PLACEHOLDER')"
            @input="searchContacts"
          />
          <div
            v-if="contactResults.length"
            class="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-lg"
          >
            <button
              v-for="contact in contactResults"
              :key="contact.id"
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-sm text-left text-n-slate-12 hover:bg-n-alpha-2"
              @click="addContact(contact)"
            >
              <span class="i-lucide-user-round size-4 text-n-slate-10" />
              {{ contact.name }}
            </button>
          </div>
          <span
            v-else-if="contactSearch && !isSearching"
            class="text-xs text-n-slate-10"
          >
            {{ t('REMINDERS.DIALOG.NO_RESULTS') }}
          </span>
        </div>
      </div>

      <!-- Reference labels -->
      <div class="flex flex-col gap-2">
        <span class="text-sm text-n-slate-11">{{
          t('REMINDERS.DIALOG.REFERENCE_LABELS')
        }}</span>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="label in availableLabels"
            :key="label.id"
            type="button"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border transition-colors"
            :class="
              form.labels.includes(label.title)
                ? 'border-n-brand text-n-slate-12 bg-n-brand/10'
                : 'bg-n-alpha-black2 border-n-weak text-n-slate-11 hover:border-n-slate-7'
            "
            @click="toggleLabel(label.title)"
          >
            <span v-if="label.icon">{{ label.icon }}</span>
            <span
              v-else
              class="size-2 rounded-full"
              :style="{ backgroundColor: label.color }"
            />
            {{ label.title }}
          </button>
          <span v-if="!availableLabels.length" class="text-xs text-n-slate-10">
            {{ t('REMINDERS.DIALOG.NO_LABELS') }}
          </span>
        </div>
      </div>

      <label class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-11">{{
          t('REMINDERS.DIALOG.DESCRIPTION')
        }}</span>
        <TextArea
          v-model="form.description"
          :placeholder="t('REMINDERS.DIALOG.DESCRIPTION_PLACEHOLDER')"
          :max-length="500"
          class="w-full"
          auto-height
        />
      </label>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          v-if="isEditing"
          :label="t('REMINDERS.DIALOG.DELETE')"
          variant="link"
          color="ruby"
          class="h-10 hover:!no-underline"
          @click="handleRemove"
        />
        <span v-else />
        <div class="flex items-center gap-3">
          <Button
            :label="t('REMINDERS.DIALOG.CANCEL')"
            variant="link"
            type="reset"
            class="h-10 hover:!no-underline hover:text-n-brand"
            @click="dialogRef?.close()"
          />
          <Button
            :label="
              isEditing
                ? t('REMINDERS.DIALOG.SAVE')
                : t('REMINDERS.DIALOG.CREATE')
            "
            color="blue"
            type="button"
            class="h-10"
            :disabled="isFormInvalid"
            @click="handleConfirm"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>
